import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CLIENT_ID } from '../lib/api';

/**
 * Keeps every open tab up to date, immediately.
 *
 * The dashboard is open on the laptop and on the classroom machine. Add a
 * to-do on one and the other should show it — not on the next reload, and not
 * in thirty seconds. The server holds a stream open per tab and writes a line
 * to it whenever anything changes.
 *
 * What arrives is a topic, never the data. This asks React Query to refetch
 * through the same authorised routes it always uses, so the stream can never
 * become a second, less careful way of reading somebody's records.
 */

/** Which cached queries a change to each part of the API invalidates. */
const TOPICS: Record<string, string[][]> = {
  todos: [['todos'], ['dashboard']],
  events: [['events'], ['dashboard']],
  habits: [['habits'], ['dashboard']],
  categories: [['categories'], ['events'], ['dashboard']],
  bookmarks: [['bookmarks'], ['dashboard']],
  notes: [['notes']],
  students: [['students'], ['seating'], ['job-board'], ['dashboard']],
  'student-flags': [['student-flags'], ['students']],
  seating: [['seating']],
  'seating-rules': [['seating-rules'], ['seating']],
  assessments: [['assessments'], ['assessment-scores'], ['dashboard']],
  'lesson-plans': [['lesson-plans'], ['sub-plans'], ['dashboard']],
  'lesson-plan-templates': [['lesson-plan-templates'], ['lesson-plans']],
  'sub-plans': [['sub-plans']],
  jobs: [['jobs'], ['job-board'], ['job-history'], ['dashboard']],
  photos: [['photos']],
  documents: [['documents'], ['dashboard']],
  'school-year': [['school-years'], ['students'], ['assessments'], ['seating'], ['auth', 'me']],
  mail: [['mail']],
  auth: [['auth', 'me']],
  backgrounds: [['backgrounds'], ['auth', 'me']],
};

export function useLiveUpdates(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    // Same origin, so the session cookie goes with it like any other request.
    const source = new EventSource('/api/live');

    source.onmessage = (message) => {
      let event: { topic?: string; origin?: string | null };
      try {
        event = JSON.parse(message.data) as typeof event;
      } catch {
        return;
      }

      /*
       * Ignore the echo of your own change. This tab already applied it —
       * refetching would replace a settled screen with a loading one for no
       * reason, and would fight an optimistic update mid-flight.
       */
      if (event.origin && event.origin === CLIENT_ID) return;

      const keys = TOPICS[event.topic ?? ''];

      if (!keys) {
        // An unrecognised topic refreshes everything active. Being wrong in
        // this direction costs a few requests; the other direction is a
        // teacher looking at a stale roster.
        void queryClient.invalidateQueries();
        return;
      }

      for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
    };

    /*
     * No handler for errors on purpose. EventSource reconnects by itself, and
     * the server sends a retry hint; logging every blip a flaky school Wi-Fi
     * produces would fill the console and change nothing.
     */

    return () => source.close();
  }, [enabled, queryClient]);
}
